/**
 * Privacy Results - Station Results Panel (Sprint 9 - AIDRIN GAP-06)
 *
 * Dedicated results view for the Privacy Risk Assessment station showing:
 * - Overall Privacy Score (0-100)
 * - k-Anonymity assessment
 * - Quasi-identifier detection
 * - Re-identification risk analysis
 * - Privacy recommendations
 *
 * Based on AIDRIN framework privacy risk assessment requirements.
 *
 * References:
 * - Sweeney, L. (2002). k-Anonymity: A Model for Protecting Privacy
 * - Hiniduma et al., "AI Data Readiness Inspector (AIDRIN)", SSDBM 2024
 */

import React, { useEffect, useState } from 'react';
import './privacy-results.css';

// ============================================================================
// TYPES (mirroring server types)
// ============================================================================

interface PrivacyRiskAssessment {
  overall_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  k_anonymity: KAnonymityResult;
  quasi_identifiers: QuasiIdentifierResult;
  reidentification_risk: ReidentificationRisk;
  recommendations: string[];
  analyzed_at: string;
  metadata: {
    total_rows: number;
    total_columns: number;
    columns_analyzed: string[];
    analysis_time_ms: number;
  };
}

interface KAnonymityResult {
  k: number;
  k_distribution: Array<{ k: number; count: number }>;
  columns_used: string[];
  unique_combinations: number;
  total_rows: number;
  pass: boolean;
  threshold: number;
  interpretation: string;
}

interface QuasiIdentifierResult {
  detected: Array<{
    column: string;
    type: 'direct' | 'quasi' | 'sensitive' | 'safe';
    uniqueness_ratio: number;
    reason: string;
  }>;
  high_risk_columns: string[];
  medium_risk_columns: string[];
  low_risk_columns: string[];
  recommendation: string;
}

interface ReidentificationRisk {
  prosecutor_risk: number;
  journalist_risk: number;
  marketer_risk: number;
  overall_risk: number;
  risk_level: string;
  interpretation: string;
}

interface PrivacyResultsProps {
  jobId: string;
  onError?: (error: string) => void;
  onRetry?: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const PrivacyResults: React.FC<PrivacyResultsProps> = ({
  jobId,
  onError,
  onRetry,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<PrivacyRiskAssessment | null>(null);

  useEffect(() => {
    fetchResults();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchResults is defined in component
  }, [jobId]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/privacy/${jobId}/results`);

      if (!response.ok) {
        throw new Error(`Failed to fetch privacy results: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status === 'failed') {
        throw new Error(data.error || 'Privacy analysis failed');
      }

      if (data.status === 'processing') {
        setTimeout(fetchResults, 2000);
        return;
      }

      setResults(data.assessment || data);
      setLoading(false);
    } catch (err: unknown) {
      console.error('[PrivacyResults] Failed to fetch results:', err);
      const errMessage = err instanceof Error ? err.message : 'Failed to fetch results';
      setError(errMessage);
      setLoading(false);
      onError?.(errMessage);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const getRiskColor = (level: string): string => {
    if (level === 'low') return 'risk-low';
    if (level === 'medium') return 'risk-medium';
    if (level === 'high') return 'risk-high';
    return 'risk-critical';
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'score-good';
    if (score >= 60) return 'score-fair';
    if (score >= 40) return 'score-warning';
    return 'score-poor';
  };

  const getRiskPercentage = (value: number): string => {
    return (value * 100).toFixed(1) + '%';
  };

  // ============================================================================
  // RENDER: LOADING
  // ============================================================================

  if (loading) {
    return (
      <div className="privacy-results loading">
        <div className="privacy-card">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Analyzing privacy risks...</p>
            <small>Checking k-anonymity, quasi-identifiers, and re-identification risks</small>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: ERROR
  // ============================================================================

  if (error) {
    return (
      <div className="privacy-results error">
        <div className="privacy-card">
          <div className="error-state">
            <span className="error-icon">&#9888;</span>
            <h3>Failed to Load Privacy Results</h3>
            <p>{error}</p>
            {onRetry && (
              <button className="btn btn-primary" onClick={onRetry}>
                Retry Analysis
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return null;
  }

  // ============================================================================
  // RENDER: RESULTS
  // ============================================================================

  const { overall_score, risk_level, k_anonymity, quasi_identifiers, reidentification_risk, recommendations, metadata } = results;

  return (
    <div className="privacy-results">
      {/* Header with Overall Score */}
      <div className="privacy-header">
        <div className="privacy-header-left">
          <h2>
            <span className="header-icon">🔒</span>
            Privacy Risk Assessment
          </h2>
          <p className="privacy-subtitle">
            AIDRIN-compliant privacy analysis for {metadata.total_rows.toLocaleString()} records
          </p>
        </div>
        <div className="privacy-overall-score">
          <div className={`score-circle ${getScoreColor(overall_score)}`}>
            <span className="score-value">{Math.round(overall_score)}</span>
            <span className="score-label">/ 100</span>
          </div>
          <span className={`risk-badge ${getRiskColor(risk_level)}`}>
            {risk_level.toUpperCase()} RISK
          </span>
        </div>
      </div>

      {/* Three Dimension Cards */}
      <div className="privacy-dimensions">
        {/* k-Anonymity Card */}
        <div className="dimension-card">
          <div className="dimension-header">
            <span className="dimension-icon">🔢</span>
            <h3>k-Anonymity</h3>
            <span className={`dimension-badge ${k_anonymity.pass ? 'pass' : 'fail'}`}>
              k = {k_anonymity.k}
            </span>
          </div>
          <div className="dimension-body">
            <div className="metric-row">
              <span className="metric-label">Status</span>
              <span className={`metric-status ${k_anonymity.pass ? 'status-pass' : 'status-fail'}`}>
                {k_anonymity.pass ? 'PASS' : 'FAIL'}
              </span>
              <span className="metric-value">
                threshold: {k_anonymity.threshold}
              </span>
            </div>
            <div className="metric-row">
              <span className="metric-label">Unique Groups</span>
              <span className="metric-value">
                {k_anonymity.unique_combinations.toLocaleString()}
              </span>
            </div>
            <div className="metric-row">
              <span className="metric-label">Columns Analyzed</span>
              <span className="metric-value">
                {k_anonymity.columns_used.length}
              </span>
            </div>

            {/* k-Distribution Chart */}
            {k_anonymity.k_distribution.length > 0 && (
              <div className="k-distribution">
                <h4>k-Value Distribution</h4>
                <div className="distribution-bars">
                  {k_anonymity.k_distribution.slice(0, 5).map((dist, idx) => {
                    const maxCount = Math.max(...k_anonymity.k_distribution.map(d => d.count));
                    const pct = (dist.count / maxCount) * 100;
                    return (
                      <div key={idx} className="distribution-bar">
                        <span className="bar-label">k={dist.k}</span>
                        <div className="bar-track">
                          <div
                            className={`bar-fill ${dist.k < k_anonymity.threshold ? 'at-risk' : 'safe'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="bar-value">{dist.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="interpretation">
              <p>{k_anonymity.interpretation}</p>
            </div>
          </div>
        </div>

        {/* Quasi-Identifiers Card */}
        <div className="dimension-card">
          <div className="dimension-header">
            <span className="dimension-icon">&#128270;</span>
            <h3>Quasi-Identifiers</h3>
            <span className={`dimension-badge ${quasi_identifiers.high_risk_columns.length > 0 ? 'fail' : 'pass'}`}>
              {quasi_identifiers.detected.length} detected
            </span>
          </div>
          <div className="dimension-body">
            {/* Risk Summary */}
            <div className="qi-summary">
              <div className="qi-stat high">
                <span className="qi-count">{quasi_identifiers.high_risk_columns.length}</span>
                <span className="qi-label">High Risk</span>
              </div>
              <div className="qi-stat medium">
                <span className="qi-count">{quasi_identifiers.medium_risk_columns.length}</span>
                <span className="qi-label">Medium Risk</span>
              </div>
              <div className="qi-stat low">
                <span className="qi-count">{quasi_identifiers.low_risk_columns.length}</span>
                <span className="qi-label">Low Risk</span>
              </div>
            </div>

            {/* Detected QIs */}
            {quasi_identifiers.detected.length > 0 && (
              <div className="qi-list">
                <h4>Detected Identifiers</h4>
                {quasi_identifiers.detected.slice(0, 5).map((qi, idx) => (
                  <div key={idx} className={`qi-row type-${qi.type}`}>
                    <span className="qi-column">{qi.column}</span>
                    <span className={`qi-type type-${qi.type}`}>{qi.type}</span>
                    <span className="qi-uniqueness">
                      {(qi.uniqueness_ratio * 100).toFixed(0)}% unique
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* High Risk Warning */}
            {quasi_identifiers.high_risk_columns.length > 0 && (
              <div className="warning-box">
                <span className="warning-icon">&#9888;</span>
                <div className="warning-content">
                  <strong>High-Risk Columns</strong>
                  <p>{quasi_identifiers.high_risk_columns.join(', ')}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Re-identification Risk Card */}
        <div className="dimension-card">
          <div className="dimension-header">
            <span className="dimension-icon">&#128373;</span>
            <h3>Re-identification Risk</h3>
            <span className={`dimension-badge ${getRiskColor(reidentification_risk.risk_level)}`}>
              {getRiskPercentage(reidentification_risk.overall_risk)}
            </span>
          </div>
          <div className="dimension-body">
            {/* Risk Types */}
            <div className="risk-types">
              <div className="risk-type">
                <span className="risk-label">Prosecutor Risk</span>
                <div className="risk-bar-track">
                  <div
                    className={`risk-bar-fill ${reidentification_risk.prosecutor_risk > 0.5 ? 'high' : reidentification_risk.prosecutor_risk > 0.2 ? 'medium' : 'low'}`}
                    style={{ width: `${reidentification_risk.prosecutor_risk * 100}%` }}
                  />
                </div>
                <span className="risk-value">{getRiskPercentage(reidentification_risk.prosecutor_risk)}</span>
              </div>
              <div className="risk-type">
                <span className="risk-label">Journalist Risk</span>
                <div className="risk-bar-track">
                  <div
                    className={`risk-bar-fill ${reidentification_risk.journalist_risk > 0.5 ? 'high' : reidentification_risk.journalist_risk > 0.2 ? 'medium' : 'low'}`}
                    style={{ width: `${reidentification_risk.journalist_risk * 100}%` }}
                  />
                </div>
                <span className="risk-value">{getRiskPercentage(reidentification_risk.journalist_risk)}</span>
              </div>
              <div className="risk-type">
                <span className="risk-label">Marketer Risk</span>
                <div className="risk-bar-track">
                  <div
                    className={`risk-bar-fill ${reidentification_risk.marketer_risk > 0.5 ? 'high' : reidentification_risk.marketer_risk > 0.2 ? 'medium' : 'low'}`}
                    style={{ width: `${reidentification_risk.marketer_risk * 100}%` }}
                  />
                </div>
                <span className="risk-value">{getRiskPercentage(reidentification_risk.marketer_risk)}</span>
              </div>
            </div>

            <div className="interpretation">
              <p>{reidentification_risk.interpretation}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="privacy-recommendations">
          <h3>
            <span className="section-icon">&#128161;</span>
            Privacy Recommendations
          </h3>
          <div className="recommendations-list">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="recommendation-item">
                <p>{rec}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="privacy-metadata">
        <span>Analyzed at: {new Date(results.analyzed_at).toLocaleString()}</span>
        <span>Analysis time: {metadata.analysis_time_ms}ms</span>
        <span>Columns analyzed: {metadata.columns_analyzed.join(', ')}</span>
      </div>
    </div>
  );
};

export default PrivacyResults;
